import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Search, SkipForward, X } from 'lucide-react';
import {
  approveImageReview,
  confirmUnreliableImageReview,
  dismissReportedImageReview,
  getImageReviews,
  rejectImageReview,
  skipImageReview
} from '../api/adminService';
import config from '../config/config';
import {
  PROVIDER_LABELS,
  getCandidateState,
  getReportedMediaUrl,
  getReviewSource,
  getReviewCandidateImageUrl
} from '../utils/imageReviewUtils';
import { getMediaUrl } from '../utils/mediaUtils';

const SECRET_STORAGE_KEY = 'forzaAdminSecret';

const CandidatePanel = ({ provider, candidate, onApprove, disabled }) => {
  const state = getCandidateState(candidate);
  const imageUrl = getReviewCandidateImageUrl(candidate, config.imagekit.urlEndpoint);

  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/50 p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white">{PROVIDER_LABELS[provider]}</h3>
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${state === 'generated' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'}`}>
          {state}
        </span>
      </div>

      <div className="aspect-square overflow-hidden rounded-md border border-white/10 bg-slate-900">
        {imageUrl ? (
          <img src={imageUrl} alt={`${PROVIDER_LABELS[provider]} candidate`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center px-4 text-center text-sm text-slate-400">
            {candidate?.error || 'No candidate generated'}
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1 text-xs text-slate-400">
        <div>Attempts: <span className="text-slate-200">{candidate?.attempts ?? 0}</span></div>
        {candidate?.extractionMethod ? <div>Extraction: <span className="text-slate-200">{candidate.extractionMethod}</span></div> : null}
      </div>

      <button
        type="button"
        disabled={disabled || state !== 'generated'}
        onClick={() => onApprove(provider)}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Check className="h-4 w-4" />
        Approve {PROVIDER_LABELS[provider]}
      </button>
    </div>
  );
};

const ReviewRow = ({ review, onApprove, onReject, onSkip, busy }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const candidates = review.candidates || {};

  return (
    <article className="rounded-lg border border-white/10 bg-slate-900/70 p-4 shadow-xl">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-white">{review.itemName}</h2>
            <span className="rounded-full bg-cyan-400/10 px-2 py-1 text-xs font-semibold text-cyan-200">{review.itemType}</span>
          </div>
          <p className="mt-2 max-w-3xl text-sm text-slate-300">{review.instructions || 'No instructions recorded.'}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(review._id)}
            className="inline-flex items-center gap-2 rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 transition hover:bg-rose-500/20 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
            Reject
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onSkip(review._id)}
            className="inline-flex items-center gap-2 rounded-md border border-slate-500/40 bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-700 disabled:opacity-40"
          >
            <SkipForward className="h-4 w-4" />
            Skip
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <CandidatePanel provider="gemini" candidate={candidates.gemini} onApprove={(provider) => onApprove(review._id, provider)} disabled={busy} />
        <CandidatePanel provider="chatgpt" candidate={candidates.chatgpt} onApprove={(provider) => onApprove(review._id, provider)} disabled={busy} />
      </div>

      <button
        type="button"
        onClick={() => setDetailsOpen((open) => !open)}
        className="mt-4 text-sm font-semibold text-cyan-300 hover:text-cyan-200"
      >
        {detailsOpen ? 'Hide prompt details' : 'View prompt details'}
      </button>

      {detailsOpen ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {['gemini', 'chatgpt'].map((provider) => (
            <pre key={provider} className="max-h-56 overflow-auto rounded-md border border-white/10 bg-slate-950 p-3 text-xs text-slate-300">
              {candidates[provider]?.promptUsed || 'No prompt recorded.'}
            </pre>
          ))}
        </div>
      ) : null}
    </article>
  );
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const formatIdList = (values = []) => {
  if (!Array.isArray(values) || !values.length) return 'None recorded';
  return values.map((value) => String(value)).join(', ');
};

const ReportedReviewRow = ({ review, onConfirmUnreliable, onDismiss, busy }) => {
  const imageUrl = getReportedMediaUrl(review, config.imagekit.urlEndpoint, config.media.workerBase);
  const canAct = review.status === 'pending';

  return (
    <article className="rounded-lg border border-amber-300/20 bg-slate-900/70 p-4 shadow-xl">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-white">{review.itemName}</h2>
            <span className="rounded-full bg-cyan-400/10 px-2 py-1 text-xs font-semibold text-cyan-200">{review.itemType}</span>
            <span className="rounded-full bg-slate-700 px-2 py-1 text-xs font-semibold text-slate-200">{review.reportedMediaType || 'image'}</span>
            <span className="rounded-full bg-amber-400/10 px-2 py-1 text-xs font-semibold text-amber-200">
              {review.reportCount || 0} {(review.reportCount || 0) === 1 ? 'report' : 'reports'}
            </span>
          </div>
          <p className="mt-2 max-w-3xl text-sm text-slate-300">{review.instructions || 'No instructions recorded.'}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            disabled={busy || !canAct}
            onClick={() => onConfirmUnreliable(review._id)}
            className="inline-flex items-center gap-2 rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 transition hover:bg-rose-500/20 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
            Confirm unreliable
          </button>
          <button
            type="button"
            disabled={busy || !canAct}
            onClick={() => onDismiss(review._id)}
            className="inline-flex items-center gap-2 rounded-md border border-slate-500/40 bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-700 disabled:opacity-40"
          >
            <SkipForward className="h-4 w-4" />
            Dismiss
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <div className="aspect-square overflow-hidden rounded-md border border-white/10 bg-slate-950">
          {imageUrl && review.reportedMediaType === 'video' ? (
            <video src={imageUrl} poster={review.reportedPosterUrl ? getMediaUrl(review.reportedPosterUrl, config.media.workerBase) : undefined} controls className="h-full w-full object-contain" />
          ) : imageUrl ? (
            <img src={imageUrl} alt={`${review.itemName} reported`} className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-slate-400">
              Reported media URL is missing.
            </div>
          )}
        </div>
        <div className="space-y-3 rounded-md border border-white/10 bg-slate-950/70 p-3 text-sm text-slate-300">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reported media</p>
            <p className="break-all text-slate-200">{review.reportedMediaUrl || review.reportedImageUrl || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reporter user IDs</p>
            <p className="break-words text-slate-200">{formatIdList(review.reporterUserIds)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Quest IDs</p>
            <p className="break-words text-slate-200">{formatIdList(review.questIds)}</p>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div>First reported: <span className="text-slate-100">{formatDateTime(review.firstReportedAt)}</span></div>
            <div>Last reported: <span className="text-slate-100">{formatDateTime(review.lastReportedAt)}</span></div>
          </div>
        </div>
      </div>
    </article>
  );
};

const AdminImageReviewPage = () => {
  const [adminSecret, setAdminSecret] = useState(() => localStorage.getItem(SECRET_STORAGE_KEY) || '');
  const [source, setSource] = useState('generated');
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('');
  const [query, setQuery] = useState('');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  const canLoad = adminSecret.trim().length > 0;

  const loadReviews = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError('');
    try {
      localStorage.setItem(SECRET_STORAGE_KEY, adminSecret);
      const response = await getImageReviews({
        adminSecret,
        status,
        source,
        type,
        q: query,
        limit: 50
      });
      setReviews(response.data?.reviews || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load image reviews.');
    } finally {
      setLoading(false);
    }
  }, [adminSecret, canLoad, query, source, status, type]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleApprove = async (reviewId, provider) => {
    setBusyId(reviewId);
    setError('');
    try {
      await approveImageReview({ adminSecret, reviewId, provider });
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to approve candidate.');
    } finally {
      setBusyId('');
    }
  };

  const handleReject = async (reviewId) => {
    setBusyId(reviewId);
    setError('');
    try {
      await rejectImageReview({ adminSecret, reviewId });
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reject review.');
    } finally {
      setBusyId('');
    }
  };

  const handleSkip = async (reviewId) => {
    setBusyId(reviewId);
    setError('');
    try {
      await skipImageReview({ adminSecret, reviewId });
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to skip review.');
    } finally {
      setBusyId('');
    }
  };

  const handleConfirmUnreliable = async (reviewId) => {
    setBusyId(reviewId);
    setError('');
    try {
      await confirmUnreliableImageReview({ adminSecret, reviewId });
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to confirm unreliable image.');
    } finally {
      setBusyId('');
    }
  };

  const handleDismissReport = async (reviewId) => {
    setBusyId(reviewId);
    setError('');
    try {
      await dismissReportedImageReview({ adminSecret, reviewId });
      await loadReviews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to dismiss image report.');
    } finally {
      setBusyId('');
    }
  };

  const countLabel = useMemo(() => `${reviews.length} ${reviews.length === 1 ? 'review' : 'reviews'}`, [reviews.length]);
  const statusOptions = source === 'reported'
    ? [
      ['pending', 'Pending'],
      ['confirmed_unreliable', 'Confirmed unreliable'],
      ['dismissed', 'Dismissed']
    ]
    : [
      ['pending', 'Pending'],
      ['approved', 'Approved'],
      ['rejected', 'Rejected'],
      ['skipped', 'Skipped']
    ];

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 text-white">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Image Review Queue</h1>
          <p className="mt-1 text-sm text-slate-400">Approve generated quest media and review user-reported inaccurate media.</p>
        </div>
        <span className="text-sm font-semibold text-slate-300">{loading ? 'Loading...' : countLabel}</span>
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-white/10 bg-slate-950 p-1">
        {[
          ['generated', 'Generated Candidates'],
          ['reported', 'Reported Media']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setSource(value);
              setStatus('pending');
            }}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition ${source === value ? 'bg-cyan-500/20 text-cyan-100' : 'text-slate-400 hover:text-slate-200'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <section className="mb-5 grid gap-3 rounded-lg border border-white/10 bg-slate-900/70 p-4 md:grid-cols-[minmax(220px,1fr)_160px_160px_minmax(220px,1fr)_auto]">
        <label className="text-sm font-semibold text-slate-300">
          Admin secret
          <input
            type="password"
            value={adminSecret}
            onChange={(event) => setAdminSecret(event.target.value)}
            className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400"
          />
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)} className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400">
            {statusOptions.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Type
          <select value={type} onChange={(event) => setType(event.target.value)} className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400">
            <option value="">All</option>
            <option value="exercise">Exercise</option>
            <option value="meal">Meal</option>
            <option value="mindfulness">Mindfulness</option>
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Search
          <div className="mt-1 flex rounded-md border border-white/10 bg-slate-950 focus-within:border-cyan-400">
            <Search className="ml-3 mt-2.5 h-4 w-4 text-slate-500" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full bg-transparent px-3 py-2 text-sm text-white outline-none"
            />
          </div>
        </label>
        <button
          type="button"
          onClick={loadReviews}
          disabled={!canLoad || loading}
          className="self-end rounded-md border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Refresh
        </button>
      </section>

      {error ? (
        <div className="mb-4 rounded-md border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200">
          {error}
        </div>
      ) : null}

      {!canLoad ? (
        <div className="rounded-lg border border-white/10 bg-slate-900/70 p-6 text-center text-slate-300">
          Enter the admin secret to load image reviews.
        </div>
      ) : reviews.length ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            getReviewSource(review) === 'reported' ? (
              <ReportedReviewRow
                key={review._id}
                review={review}
                busy={busyId === review._id}
                onConfirmUnreliable={handleConfirmUnreliable}
                onDismiss={handleDismissReport}
              />
            ) : (
              <ReviewRow
                key={review._id}
                review={review}
                busy={busyId === review._id}
                onApprove={handleApprove}
                onReject={handleReject}
                onSkip={handleSkip}
              />
            )
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-slate-900/70 p-6 text-center text-slate-300">
          No image reviews found.
        </div>
      )}
    </main>
  );
};

export default AdminImageReviewPage;
